import { useState, useEffect } from "react";
import { BarChart3 } from "lucide-react";
import { getApiUrl } from "@/config/api";
import { authenticatedFetch } from "@/utils/apiUtils";
import { DataHealthResponse } from "@/types";

function CoverageBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-yellow-500" : "bg-red-500";

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-20 cgm-text-secondary">{label}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right cgm-text-primary font-mono text-[11px]">{pct}%</span>
    </div>
  );
}

interface DataHealthCardProps {
  /** When provided, card shows only this team's row.
   *  Used by the coach Team Overview tab, which is scoped to a single selected team. */
  teamId?: number;
}

export default function DataHealthCard({ teamId }: DataHealthCardProps = {}) {
  const [data, setData] = useState<DataHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      const result = await authenticatedFetch<DataHealthResponse>(
        getApiUrl("/api/data-health/"),
        { method: "GET" },
        "fetching data health"
      );
      if (result.success && result.data) {
        setData(result.data);
      }
      setLoading(false);
    };
    run();
  }, []);

  if (loading) return <div className="cgm-card p-4 cgm-text-secondary text-sm">Loading data health...</div>;
  if (!data) return null;

  const teams = teamId !== undefined
    ? data.teams.filter((t) => t.id === teamId)
    : data.teams;

  if (teams.length === 0) return null;

  return (
    <div className="cgm-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 cgm-text-secondary" />
        <h3 className="text-sm font-semibold cgm-text-primary">Data Health</h3>
      </div>

      {teams.map((team) => (
        <div key={team.id} className="space-y-2">
          <div className="flex justify-between text-xs">
            <span className="cgm-text-primary font-medium">{team.name}</span>
            <span className="cgm-text-secondary">{team.athlete_count} athletes</span>
          </div>

          <CoverageBar label="Catapult" value={team.signal_coverage.catapult} />
          <CoverageBar label="Wearable" value={team.signal_coverage.wearable} />
          <CoverageBar label="Check-ins" value={team.signal_coverage.subjective} />

          <div className="flex items-center gap-2 text-xs pt-1">
            <span className="cgm-text-secondary">Injury records:</span>
            <span className="cgm-text-primary font-medium">{team.injury_records}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
